// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import {
  MetricDivider,
  MetricRow,
  PrimaryMetric,
  SummaryCard,
} from '@/components/summary-card';
import { IsoCurrencyCode } from '@/currency/constants/iso';
import { formatAmountWithCurrency } from '@/currency/utilities/format';
import { InventoryCurrencyStats } from '@/report/api/get-inventory-assets-snapshot-report';
import { formatNumber } from '@/utilities/number';
import { pluralize } from '@/utilities/pluralize';
import { Grid } from '@mui/material';

interface Props {
  stats: InventoryCurrencyStats;
  currencyCode: IsoCurrencyCode;
  isLoading: boolean;
}

/**
 * Renders the inventory snapshot summary cards for a single cost-currency bucket.
 * Stats are computed by the backend per currency, so no total here ever mixes
 * currencies; amounts are formatted in the bucket's isoCode.
 */
export function InventorySummaryCards(props: Props) {
  return (
    <Grid container spacing={3}>
      {/* Asset Overview Card */}
      <Grid item xs={12} md={4}>
        <SummaryCard>
          <PrimaryMetric
            label="Total inventory assets"
            value={formatNumber(props.stats.assetCount)}
            isLoading={props.isLoading}
          />
          <MetricDivider>
            <MetricRow
              label="Complete records"
              value={formatNumber(props.stats.assetsWithCompleteAccounting)}
              color="success.main"
              isLoading={props.isLoading}
            />
            <MetricRow
              label="Incomplete records"
              value={formatNumber(props.stats.assetsWithIncompleteAccounting)}
              color="error.main"
              isLoading={props.isLoading}
            />
          </MetricDivider>
        </SummaryCard>
      </Grid>

      {/* Financial Summary Card */}
      <Grid item xs={12} md={4}>
        <SummaryCard>
          <PrimaryMetric
            label="Total book value"
            value={formatAmountWithCurrency(
              props.stats.totalBookValue,
              props.currencyCode,
              { sparse: true }
            )}
            isLoading={props.isLoading}
          />
          <MetricDivider>
            <MetricRow
              label="Expected depreciable amount"
              value={formatAmountWithCurrency(
                props.stats.totalDepreciableAmount,
                props.currencyCode,
                { sparse: true }
              )}
              isLoading={props.isLoading}
            />
            <MetricRow
              label="Expected salvage value"
              value={formatAmountWithCurrency(
                props.stats.totalExpectedSalvageValue,
                props.currencyCode,
                { sparse: true }
              )}
              isLoading={props.isLoading}
            />
          </MetricDivider>
        </SummaryCard>
      </Grid>

      {/* Performance Metrics Card */}
      <Grid item xs={12} md={4}>
        <SummaryCard>
          <PrimaryMetric
            label="Avg. inventory days"
            value={formatNumber(props.stats.weightedInventoryDays, {
              placeholder: '—',
            })}
            isLoading={props.isLoading}
            tooltip="Weighted average number of days in inventory (dollar-weighted by asset cost basis)"
          />
          <MetricDivider>
            <MetricRow
              label="Avg. useful life"
              value={
                props.stats.weightedUsefulLife !== null
                  ? pluralize(props.stats.weightedUsefulLife, {
                      singular: 'month',
                      plural: 'months',
                    })
                  : '—'
              }
              isLoading={props.isLoading}
              tooltip="Weighted average useful life of inventory assets (dollar-weighted by asset cost basis)"
            />
          </MetricDivider>
        </SummaryCard>
      </Grid>
    </Grid>
  );
}
